import React, { useState, useEffect } from 'react';
import {
  AreaChart, Area, BarChart, Bar, XAxis, YAxis,
  CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell,
} from 'recharts';
import { Ic, ICONS } from '../data/icons';
import { DEVICES, ACTIVITIES, ALERTS, ENROLL_DATA, COMPLIANCE_DATA, PIE_DATA, COMPLIANCE_MAP, DEVICE_ICON, DEVICE_COLOR } from '../data/mockData';
import { Badge, StatusDot, MiniBar, ChartTooltip, Card, CardHeader } from '../components/shared';

export default function Dashboard({ C, isMobile }) {
  const [anim, setAnim] = useState(false);
  useEffect(() => { const t = setTimeout(() => setAnim(true), 150); return () => clearTimeout(t); }, []);

  const STATS = [
    { label: "Toplam Cihaz", value: 247, change: 12,  icon: "devices", color: "#3b82f6", bg: "rgba(59,130,246,0.12)",  sub: "Son 30 günde +12" },
    { label: "Çevrimiçi",    value: 198, change: 5,   icon: "wifi",    color: "#22c55e", bg: "rgba(34,197,94,0.12)",   sub: "%80.2 çevrimiçi" },
    { label: "Uyumlu",       value: 231, change: -3,  icon: "check",   color: "#a78bfa", bg: "rgba(167,139,250,0.12)", sub: "%93.5 uyumluluk" },
    { label: "Aktif İhlal",  value: 6,   change: 2,   icon: "alert",   color: "#ef4444", bg: "rgba(239,68,68,0.12)",   sub: "Hemen aksiyon al" },
  ];

  const alertColor = { critical: C.red, warning: C.amber, info: C.accent };
  const alertBg    = { critical: C.redBg, warning: C.amberBg, info: C.accentBg };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>

      {/* Stat Cards */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(180px,1fr))", gap: 13 }}>
        {STATS.map(s => {
          const isNeg = s.label.includes("İhlal");
          const isUp  = s.change > 0;
          const cc    = isNeg ? (isUp ? C.red : C.green) : (isUp ? C.green : C.red);
          return (
            <div key={s.label} style={{
              background: C.surface, border: `1px solid ${C.border}`, borderRadius: 14,
              padding: "18px 20px", display: "flex", flexDirection: "column", gap: 14,
              boxShadow: C.shadowSm, transition: "transform 0.18s,box-shadow 0.18s",
              cursor: "default", position: "relative", overflow: "hidden",
            }}
              onMouseEnter={e => { e.currentTarget.style.transform = "translateY(-2px)"; e.currentTarget.style.boxShadow = C.shadow; }}
              onMouseLeave={e => { e.currentTarget.style.transform = "none"; e.currentTarget.style.boxShadow = C.shadowSm; }}
            >
              <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between" }}>
                <div style={{ width: 42, height: 42, borderRadius: 12, background: s.bg, display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                  <Ic d={ICONS[s.icon]} size={20} s={s.color} />
                </div>
                <div style={{ fontSize: 11, fontWeight: 600, padding: "3px 8px", borderRadius: 99,
                  background: isNeg === isUp ? C.redBg : C.greenBg,
                  color: cc, display: "flex", alignItems: "center", gap: 3 }}>
                  <Ic d={isUp ? ICONS.up : ICONS.down} size={10} s={cc} /> {Math.abs(s.change)}
                </div>
              </div>
              <div>
                <div style={{ fontSize: 30, fontWeight: 800, color: C.text, letterSpacing: "-1.5px", lineHeight: 1 }}>{s.value}</div>
                <div style={{ fontSize: 12.5, color: C.textSub, marginTop: 5, fontWeight: 500 }}>{s.label}</div>
                <div style={{ fontSize: 11, color: C.textFaint, marginTop: 3 }}>{s.sub}</div>
              </div>
              <div style={{ position: "absolute", right: -20, bottom: -20, width: 80, height: 80, borderRadius: "50%", background: s.bg, opacity: 0.6, pointerEvents: "none" }} />
            </div>
          );
        })}
      </div>

      {/* Alert Banner */}
      <Card C={C}>
        <CardHeader C={C} title={
          <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
            <Ic d={ICONS.zap} size={15} s={C.amber} />
            <span>Aktif Uyarılar</span>
          </div>
        } action={<span style={{ fontSize: 11, color: C.accent, cursor: "pointer", fontWeight: 600 }}>Tümünü Gör →</span>} />
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(200px,1fr))" }}>
          {ALERTS.map((a, i) => (
            <div key={a.id} style={{
              padding: "12px 18px", display: "flex", alignItems: "center", gap: 12,
              cursor: "pointer", borderRight: i < ALERTS.length - 1 ? `1px solid ${C.border}` : "none",
              transition: "background 0.15s",
            }}
              onMouseEnter={e => e.currentTarget.style.background = C.surface2}
              onMouseLeave={e => e.currentTarget.style.background = "transparent"}
            >
              <div style={{ width: 34, height: 34, borderRadius: 9, flexShrink: 0, background: alertBg[a.severity], display: "flex", alignItems: "center", justifyContent: "center" }}>
                <Ic d={ICONS.alert} size={15} s={alertColor[a.severity]} />
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12.5, color: C.text, marginBottom: 4, lineHeight: 1.4 }}>{a.msg}</div>
                <Badge color={alertColor[a.severity]} small>{a.count} cihaz</Badge>
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* Charts Row */}
      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 1fr", gap: 14 }}>
        {/* Enrollment Chart */}
        <Card C={C}>
          <div style={{ padding: "14px 20px 8px", borderBottom: `1px solid ${C.border}` }}>
            <div style={{ fontWeight: 700, color: C.text, fontSize: 13.5, marginBottom: 2 }}>Haftalık Kayıt Aktivitesi</div>
            <div style={{ fontSize: 12, color: C.textFaint }}>Son 7 günde kayıt ve çıkış</div>
          </div>
          <div style={{ padding: "12px 6px 8px" }}>
            <ResponsiveContainer width="100%" height={170}>
              <AreaChart data={ENROLL_DATA} margin={{ top: 5, right: 10, left: -20, bottom: 0 }}>
                <defs>
                  <linearGradient id="g1" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#3b82f6" stopOpacity={0} />
                  </linearGradient>
                  <linearGradient id="g2" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#ef4444" stopOpacity={0.25} />
                    <stop offset="95%" stopColor="#ef4444" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke={C.chartGrid} />
                <XAxis dataKey="day" tick={{ fill: C.textFaint, fontSize: 11 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: C.textFaint, fontSize: 11 }} axisLine={false} tickLine={false} />
                <Tooltip content={<ChartTooltip />} />
                <Area type="monotone" dataKey="kayıt" stroke="#3b82f6" strokeWidth={2} fill="url(#g1)" dot={{ fill: "#3b82f6", r: 3 }} />
                <Area type="monotone" dataKey="çıkış" stroke="#ef4444" strokeWidth={2} fill="url(#g2)" dot={{ fill: "#ef4444", r: 3 }} />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </Card>

        {/* Compliance Chart */}
        <Card C={C}>
          <div style={{ padding: "14px 20px 8px", borderBottom: `1px solid ${C.border}` }}>
            <div style={{ fontWeight: 700, color: C.text, fontSize: 13.5, marginBottom: 2 }}>Uyumluluk Trendi</div>
            <div style={{ fontSize: 12, color: C.textFaint }}>4 haftada uyumluluk dağılımı</div>
          </div>
          <div style={{ padding: "12px 6px 8px" }}>
            <ResponsiveContainer width="100%" height={170}>
              <BarChart data={COMPLIANCE_DATA} margin={{ top: 5, right: 10, left: -20, bottom: 0 }} barSize={12}>
                <CartesianGrid strokeDasharray="3 3" stroke={C.chartGrid} />
                <XAxis dataKey="h" tick={{ fill: C.textFaint, fontSize: 11 }} axisLine={false} tickLine={false} />
                <YAxis tick={{ fill: C.textFaint, fontSize: 11 }} axisLine={false} tickLine={false} />
                <Tooltip content={<ChartTooltip />} />
                <Bar dataKey="uyumlu" name="Uyumlu" fill="#22c55e" radius={[4, 4, 0, 0]} />
                <Bar dataKey="uyarı"  name="Uyarı"  fill="#f59e0b" radius={[4, 4, 0, 0]} />
                <Bar dataKey="ihlal"  name="İhlal"  fill="#ef4444" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>

      {/* Bottom Row */}
      <div style={{ display: "grid", gridTemplateColumns: isMobile ? "1fr" : "1fr 290px 270px", gap: 14 }}>

        {/* Device Table */}
        <Card C={C}>
          <CardHeader C={C} title="Son Cihazlar"
            action={<span style={{ fontSize: 11, color: C.accent, cursor: "pointer", fontWeight: 600 }}>Tümünü Gör →</span>} />
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", minWidth: 440 }}>
              <thead>
                <tr style={{ background: C.surface2 }}>
                  {["Cihaz", "Kullanıcı", "Depolama", "Durum", "Uyumluluk"].map(h => (
                    <th key={h} style={{ textAlign: "left", padding: "9px 16px", fontSize: 10.5, fontWeight: 700, color: C.textFaint, letterSpacing: "0.7px", textTransform: "uppercase", borderBottom: `1px solid ${C.border}` }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {DEVICES.slice(0, 6).map(d => {
                  const [cColor, cLabel] = COMPLIANCE_MAP[d.compliance] || ["#94a3b8", d.compliance];
                  const sc = d.storage > 80 ? C.red : d.storage > 60 ? C.amber : C.green;
                  return (
                    <tr key={d.id} style={{ cursor: "pointer", transition: "background 0.1s" }}
                      onMouseEnter={e => e.currentTarget.style.background = C.surface2}
                      onMouseLeave={e => e.currentTarget.style.background = "transparent"}>
                      <td style={{ padding: "11px 16px", borderBottom: `1px solid ${C.border}` }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 9 }}>
                          <div style={{ width: 30, height: 30, borderRadius: 8, flexShrink: 0, background: `${DEVICE_COLOR[d.type]}18`, display: "flex", alignItems: "center", justifyContent: "center", color: DEVICE_COLOR[d.type] }}>
                            <Ic d={ICONS[DEVICE_ICON[d.type]]} size={14} />
                          </div>
                          <div>
                            <div style={{ fontSize: 12.5, fontWeight: 600, color: C.text }}>{d.name}</div>
                            <div style={{ fontSize: 10, color: C.textFaint }}>{d.os}</div>
                          </div>
                        </div>
                      </td>
                      <td style={{ padding: "11px 16px", borderBottom: `1px solid ${C.border}` }}>
                        <div style={{ fontSize: 12.5, color: C.textSub }}>{d.user}</div>
                        <div style={{ fontSize: 10, color: C.textFaint }}>{d.dept}</div>
                      </td>
                      <td style={{ padding: "11px 16px", borderBottom: `1px solid ${C.border}`, minWidth: 90 }}>
                        <div style={{ fontSize: 11, color: C.textFaint, marginBottom: 5 }}>{d.storage}%</div>
                        <MiniBar pct={anim ? d.storage : 0} color={sc} C={C} />
                      </td>
                      <td style={{ padding: "11px 16px", borderBottom: `1px solid ${C.border}` }}>
                        <div style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 12, color: C.textSub }}>
                          <StatusDot online={d.status === "online"} /> {d.status === "online" ? "Çevrimiçi" : "Çevrimdışı"}
                        </div>
                      </td>
                      <td style={{ padding: "11px 16px", borderBottom: `1px solid ${C.border}` }}>
                        <Badge color={cColor}>{cLabel}</Badge>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </Card>

        {/* Pie Chart */}
        <Card C={C}>
          <CardHeader C={C} title="Platform Dağılımı" />
          <div style={{ padding: 16 }}>
            <ResponsiveContainer width="100%" height={130}>
              <PieChart>
                <Pie data={PIE_DATA} cx="50%" cy="50%" innerRadius={34} outerRadius={58} dataKey="value" paddingAngle={3} stroke="none">
                  {PIE_DATA.map((e, i) => <Cell key={i} fill={e.color} />)}
                </Pie>
                <Tooltip formatter={(v, n) => [`${v} cihaz`, n]} contentStyle={{ background: "#1e293b", border: "none", borderRadius: 8, fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
            <div style={{ display: "flex", flexDirection: "column", gap: 9, marginTop: 6 }}>
              {PIE_DATA.map(p => (
                <div key={p.name} style={{ display: "flex", alignItems: "center", gap: 9 }}>
                  <span style={{ width: 10, height: 10, borderRadius: 3, background: p.color, flexShrink: 0 }} />
                  <span style={{ fontSize: 12, color: C.textSub, flex: 1 }}>{p.name}</span>
                  <span style={{ fontSize: 12, fontWeight: 700, color: C.text }}>{p.value}</span>
                </div>
              ))}
            </div>
          </div>
        </Card>

        {/* Activity Feed */}
        <Card C={C}>
          <CardHeader C={C} title="Son Aktiviteler" />
          <div>
            {ACTIVITIES.map((a, i) => (
              <div key={a.id} style={{
                display: "flex", alignItems: "flex-start", gap: 10, padding: "11px 16px",
                cursor: "pointer", borderBottom: i < ACTIVITIES.length - 1 ? `1px solid ${C.border}` : "none",
                transition: "background 0.1s",
              }}
                onMouseEnter={e => e.currentTarget.style.background = C.surface2}
                onMouseLeave={e => e.currentTarget.style.background = "transparent"}
              >
                <div style={{ width: 30, height: 30, borderRadius: 8, flexShrink: 0, background: `${a.color}20`, display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <Ic d={ICONS[a.icon]} size={13} s={a.color} />
                </div>
                <div style={{ flex: 1, minWidth: 0 }}>
                  <div style={{ fontSize: 12, color: C.text, lineHeight: 1.4, marginBottom: 2 }}>{a.msg}</div>
                  <div style={{ fontSize: 10.5, color: C.textFaint, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.detail}</div>
                  <div style={{ fontSize: 10, color: C.textFaint, marginTop: 2 }}>{a.time} önce</div>
                </div>
              </div>
            ))}
          </div>
        </Card>
      </div>
    </div>
  );
}
